import React, { useEffect, useState, useContext } from "react";
import useCeo from "../../hooks/useCEO";
import { useUnidades } from "../../hooks/test/useUnidades";
import { AuthContext } from "../../context/authContext/authContext";
import Filtros from "../../components/Filtros";
import { DateTime } from "luxon";
import MUIDataTable from "mui-datatables";
import { IconButton, Popover, Box } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import RemoveIcon from "@mui/icons-material/Remove";
import { Spinner } from "../../components/Spinner";
import moment from "moment-timezone";

// Toolbar con popup que recibe props
const ToolbarPopup = ({ negocioId, fechaI, fechaF, handleChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) =>
    setAnchorEl(anchorEl ? null : event.currentTarget);

  const open = Boolean(anchorEl);
  const id = open ? "popup-empty" : undefined;

  return (
    <>
      <IconButton
        onClick={handleClick}
        title={open ? "Cerrar" : "Abrir" + " Filtros Cliente"}
      >
        {open ? <RemoveIcon /> : <TuneIcon />}
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ bgcolor: "white" }} />
        <div style={{ padding: 16, maxWidth: 600 }}>
          <Filtros
            config={["negocio", "fechas"]}
            valores={{ negocioId, fechaI, fechaF }}
            onChange={handleChange}
          />
        </div>
      </Popover>
    </>
  );
};

export const Bitacora = () => {
  useCeo({ title: "Bitacora", description: "Página de bitacora" });
  const fechaServidor = DateTime.now()
    .setZone("America/Mexico_City")
    .toJSDate();

  const [data, setData] = useState([]);
  const { user } = useContext(AuthContext);
  const [negocioId, setNegocioId] = useState(user.negocio_id);
  const [cliente, setCliente] = useState("");
  const [fechaI, setFechaI] = useState(fechaServidor);
  const [fechaF, setFechaF] = useState(fechaServidor);
  const [loading, setLoading] = useState(false);

  const { getBitacorasV2 } = useUnidades();

  useEffect(() => {
    fetchBitacoras();
  }, [negocioId, fechaI, fechaF]);

  const fetchBitacoras = async () => {
    if (!negocioId) return;

    setData([]);
    try {
      setLoading(true);
      const resp = await getBitacorasV2(
        negocioId,
        formatDate(fechaI),
        formatDate(fechaF),
      );
      if (resp.ok && Array.isArray(resp.bitacoras)) {
        const array = resp.bitacoras.map((b) => [
          b.usuario,
          b.rol,
          b.unidad,
          b.origen,
          b.destinos.join(", "),
          moment
            .utc(b.fecha_hora_salida)
            .tz("America/Mexico_City")
            .format("lll") || "",
          b.operador,
          b.tripulacion.length > 0 ? b.tripulacion.join(", ") : "N/A",
          b.estatus,
          moment.utc(b.hora_estatus).tz("America/Mexico_City").format("lll"),
          b.coordenadas,
          b.observaciones,
          b.semaforo,
          b.preba_paro_motor ? "Sí" : !b.preba_paro_motor ? "No" : "N/A",
          b.prueba_boton_panico ? "Sí" : !b.prueba_boton_panico ? "No" : "N/A",
          b.prueba_conectividad ? "Sí" : !b.prueba_conectividad ? "No" : "N/A",
        ]);
        setData(array);
        setCliente(resp.cliente);
      } else {
        setData([]);
        setCliente("");
      }
      setLoading(false);
    } catch (error) {
      console.error("Error al cargar bitacora:", error);
      setLoading(false);
    }
  };

  const handleChange = (campo, valor) => {
    if (campo === "negocio") setNegocioId(valor);
    else if (campo === "fechaI") setFechaI(valor || null);
    else if (campo === "fechaF") setFechaF(valor || null);
  };

  const columns = [
    { name: "Usuario", label: "Usuario" },
    { name: "Rol", label: "Rol" },
    { name: "Unidad", label: "Unidad" },
    { name: "Origen", label: "Origen" },
    { name: "Destinos", label: "Destinos" },
    { name: "Fecha/Hora Salida", label: "Fecha/Hora Salida" },
    { name: "Operador", label: "Operador" },
    { name: "Tripulacion", label: "Tripulacion" },
    { name: "Estatus", label: "Estatus" },
    { name: "Hora Estatus", label: "Hora Estatus" },
    { name: "Coordenadas", label: "Coordenadas" },
    { name: "Observaciones", label: "Observaciones" },
    {
      name: "Semaforo Minutos",
      label: "Semaforo Minutos",
      options: {
        display: "excluded",
        filter: false,
        sort: false,
        download: true,
      },
    },
    {
      name: "Paro Motor",
      label: "Paro Motor",
      options: {
        display: "excluded",
        filter: false,
        sort: false,
        download: true,
      },
    },
    {
      name: "Botón Pánico",
      label: "Botón Pánico",
      options: {
        display: "excluded",
        filter: false,
        sort: false,
        download: true,
      },
    },
    {
      name: "Conectividad",
      label: "Conectividad",
      options: {
        display: "excluded",
        filter: false,
        sort: false,
        download: true,
      },
    },
  ];

  const options = {
    selectableRows: "none",
    responsive: "standard",
    fixedHeader: true,
    downloadOptions: {
      filename: cliente
        ? `Bitácora de movimientos ${cliente}`
        : "Sin información",
      filterOptions: {
        useDisplayedColumnsOnly: false,
        useDisplayedRowsOnly: true,
      },
      charset: "utf-8",
    },
    onDownload: (buildHead, buildBody, columns, data) =>
      "\uFEFF" + buildHead(columns) + buildBody(data),
    customToolbar: () =>
      [1, 2].includes(parseInt(user.negocio_id)) ? (
        <ToolbarPopup
          negocioId={negocioId}
          fechaI={fechaI}
          fechaF={fechaF}
          handleChange={handleChange}
        />
      ) : null,
    textLabels: {
      body: {
        noMatch: "No se encontraron registros",
        toolTip: "Ordenar",
        columnHeaderTooltip: (column) => `Ordenar por ${column.label}`,
      },
      pagination: {
        next: "Página siguiente",
        previous: "Página anterior",
        rowsPerPage: "Filas por página:",
        displayRows: "de",
      },
      toolbar: {
        search: "Buscar",
        downloadCsv: "Descargar CSV",
        print: "Imprimir",
        viewColumns: "Ver columnas",
        filterTable: "Filtro de tabla",
      },
      filter: { all: "TODOS", title: "FILTROS", reset: "LIMPIAR" },
      viewColumns: {
        title: "Mostrar columnas",
        titleAria: "Mostrar/Ocultar columnas de tabla",
      },
      selectedRows: {
        text: "Fila(s) seleccionada(s)",
        delete: "Remover",
        deleteAria: "Fila seleccionada removida",
      },
    },
  };

  // const formatDate = (date) => (date ? date.toISOString().split("T")[0] : null);
  const formatDate = (date) =>
    date
      ? DateTime.fromJSDate(date)
          .setZone("America/Mexico_City")
          .toFormat("yyyy-MM-dd")
      : null;

  return (
    <div className="m-1">
      {loading && <Spinner />}
      <MUIDataTable
        title={loading ? "Cargando..." : `Bitácora de movimientos ${cliente}`}
        data={data}
        columns={columns}
        options={options}
      />
    </div>
  );
};
